'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 60_000;
const SEEN_IDS_KEY = 'subscription-notification-seen-ids';

const getSeenIds = (): string[] => {
    try {
        return JSON.parse(localStorage.getItem(SEEN_IDS_KEY) || '[]');
    } catch {
        return [];
    }
};

interface SubscriptionBannerItem {
    id: string;
}

interface ProfileNotificationMarkerProps {
    initialUnreadCount: number;
    className?: string;
}

const ProfileNotificationMarker = ({
    initialUnreadCount,
    className = '',
}: ProfileNotificationMarkerProps) => {
    const pathname = usePathname();
    const [unreadCount, setUnreadCount] = useState(initialUnreadCount);
    const [subscriptionBanners, setSubscriptionBanners] = useState<
        SubscriptionBannerItem[]
    >([]);
    const [surveyReminderBanners, setSurveyReminderBanners] = useState<
        SubscriptionBannerItem[]
    >([]);
    const [seenSubscriptionIds, setSeenSubscriptionIds] = useState<string[]>(
        () => (typeof window !== 'undefined' ? getSeenIds() : []),
    );

    const lang = pathname.split('/')[1] || 'th';
    const isSurveyPage = /\/profile\/surveys(?:\/|$)/.test(pathname);
    const isProfilePage = /^(?:\/[a-z]{2})?\/profile\/?$/.test(pathname);
    const isSubscriptionPage =
        /^(?:\/[a-z]{2})?\/profile\/subscriptions\/?$/.test(pathname);

    const fetchUnreadCount = useCallback(async () => {
        try {
            const res = await fetch('/api/user-notifications/unread-count');
            const data = await res.json();
            setUnreadCount(data.unreadCount ?? 0);
        } catch (error) {
            console.error('[ProfileNotificationMarker] fetch error:', error);
        }
    }, []);

    const fetchSubscriptionBanners = useCallback(async () => {
        try {
            const res = await fetch(
                `/api/profile-banners/subscription-expiry?lang=${lang}`,
            );
            const data = await res.json();
            setSubscriptionBanners(Array.isArray(data.items) ? data.items : []);
        } catch (err) {
            console.error(
                '[ProfileNotificationMarker] subscription banner fetch error:',
                err,
            );
        }
    }, [lang]);

    const fetchSurveyReminderBanners = useCallback(async () => {
        try {
            const res = await fetch(
                `/api/profile-banners/subscription-survey-reminder?lang=${lang}`,
            );
            const data = await res.json();
            setSurveyReminderBanners(
                Array.isArray(data.items) ? data.items : [],
            );
        } catch (err) {
            console.error(
                '[ProfileNotificationMarker] survey reminder banner fetch error:',
                err,
            );
        }
    }, [lang]);

    useEffect(() => {
        fetchUnreadCount();
        fetchSubscriptionBanners();
        fetchSurveyReminderBanners();
        const timer = window.setInterval(() => {
            void fetchUnreadCount();
            void fetchSubscriptionBanners();
            void fetchSurveyReminderBanners();
        }, POLL_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [
        fetchUnreadCount,
        fetchSubscriptionBanners,
        fetchSurveyReminderBanners,
    ]);

    useEffect(() => {
        const handleNotificationRead = () => void fetchUnreadCount();
        window.addEventListener(
            'user-notification-read',
            handleNotificationRead,
        );
        return () =>
            window.removeEventListener(
                'user-notification-read',
                handleNotificationRead,
            );
    }, [fetchUnreadCount]);

    useEffect(() => {
        if (
            (!isProfilePage && !isSubscriptionPage) ||
            subscriptionBanners.length === 0
        )
            return;
        setSeenSubscriptionIds((prev) => {
            const combined = prev.concat(subscriptionBanners.map((b) => b.id));
            const merged = combined.filter(
                (id, i) => combined.indexOf(id) === i,
            );
            localStorage.setItem(SEEN_IDS_KEY, JSON.stringify(merged));
            return merged;
        });
    }, [isProfilePage, isSubscriptionPage, subscriptionBanners]);

    useEffect(() => {
        if (!isProfilePage || surveyReminderBanners.length === 0) return;
        setSeenSubscriptionIds((prev) => {
            const combined = prev.concat(
                surveyReminderBanners.map((b) => b.id),
            );
            const merged = combined.filter(
                (id, i) => combined.indexOf(id) === i,
            );
            localStorage.setItem(SEEN_IDS_KEY, JSON.stringify(merged));
            return merged;
        });
    }, [isProfilePage, isSurveyPage, surveyReminderBanners]);

    const hasUnreadNotification = !isSurveyPage && unreadCount > 0;
    const hasSubscriptionBanner = subscriptionBanners.some(
        (item) => !seenSubscriptionIds.includes(item.id),
    );
    const hasSurveyReminderBanner = surveyReminderBanners.some(
        (item) => !seenSubscriptionIds.includes(item.id),
    );

    if (
        !hasUnreadNotification &&
        !hasSubscriptionBanner &&
        !hasSurveyReminderBanner
    )
        return null;

    return <span aria-hidden="true" className={className} />;
};

export default ProfileNotificationMarker;
